// packages/web-ui/src/components/kb-change-tree/KbChangeTree.tsx
//
// Story W4-S4 AC #1, #5–#8 — orchestrator component for the KB change-tree.
// The single component used both inline in chat (mode="inline", ≤640 px) and
// full-width in the Diff destination tab (mode="full").
//
// Source-of-truth is the `changeset` zustand slice (state/changeset.ts).
// Tests inject a vanilla store via the `store` prop; production callers
// pass the root-store-bound store from state/store.ts (W4-S4 does not
// modify state/store.ts — wiring is owned by the parent merge story).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import type { StoreApi } from 'zustand';
import type {
  KbChangeset,
  KbEntityChange,
  KbEntityState,
  KbEntityStatus,
  Result,
} from '@tessa-lean/base';

import {
  KIND_BADGE_LABEL,
  KIND_GROUP_LABEL,
  counts,
  findBlockingAncestor,
  groupedEntities,
  statusFor,
  statusIcon,
} from './kb-change-tree-helpers.js';
import { ReviewChip } from './ReviewChip.js';
import {
  ReviewChipContextMenu,
  type ReviewChipMenuAction,
} from './ReviewChipContextMenu.js';
import { RowDisclosure } from './RowDisclosure.js';

import type {
  AvailableUndoChangeset,
  ChangesetSlice,
  UndoInFlight,
} from '../../state/changeset.js';
import { createChangesetStore } from '../../state/changeset.js';
import { changesetApi } from '../../api/clients/changeset.js';
import type { ApiContext, ApiError } from '../../api/client.js';

// ---------------------------------------------------------------------------
// Types & default store
// ---------------------------------------------------------------------------

export interface KbChangeTreeProps {
  changesetId: string;
  /** 'inline' = chat ≤640 px (UX §6.1.5); 'full' = Diff tab ≥720 px. Default 'full'. */
  mode?: 'inline' | 'full';
  /** DR-3: when true, accepting a child auto-accepts its `dependsOn` ancestors. Default true. */
  autoAcceptAncestors?: boolean;
  /** Default true. Reserved for future virtualisation; the helpers honour it. */
  lazyPreviews?: boolean;
  /** Honour `prefers-reduced-motion: reduce`. Default `auto-detect`. */
  reducedMotion?: boolean;
  /** Test seam — inject a custom store. Defaults to a singleton-scoped store. */
  store?: StoreApi<ChangesetSlice>;
  /** Test seam — override the SVG preview fetch. */
  fetchPreview?: (changesetId: string, entityId: string) => Promise<Result<string, ApiError>>;
  /** Test seam — override the api context for the default-store. */
  apiContext?: ApiContext;
  /** Test seam — override the file-open hand-off. */
  onOpenFile?: (entity: KbEntityChange) => void;
  /** Test seam — override the syside hand-off. */
  onOpenInSyside?: (entity: KbEntityChange) => void;
  /** Whether the syside-IDE menu item should be visible. Default true. */
  sysideAvailable?: boolean;
}

// Module-level fallback store. Production callers should pass `store` from
// state/store.ts; this default exists so the component is renderable
// without root-store wiring (the DEV 2-2 owner has not yet composed the
// changeset slice into the root store).
let _defaultStore: StoreApi<ChangesetSlice> | null = null;
function getOrCreateDefaultStore(apiContext?: ApiContext): StoreApi<ChangesetSlice> {
  _defaultStore ??= createChangesetStore(apiContext ? { apiContext } : {});
  return _defaultStore;
}

/** Test-only — reset the module-level singleton between specs. */
export function _resetDefaultStoreForTests(): void {
  _defaultStore = null;
}

/**
 * Background colour for the numbered `[N]` badge that appears at the start
 * of each entity row. The colour mirrors the change-kind palette used by
 * the `<ChangesetPreviewCard>` diagram (emerald for add, amber for update,
 * rose for remove) so the user can visually match each list row to the
 * matching badge on the visual diff above.
 */
function changeIndexBadgeBg(op: KbEntityChange['op']): string {
  switch (op) {
    case 'add':
      return '#10B981'; // emerald-500
    case 'update':
      return '#F59E0B'; // amber-500
    case 'remove':
      return '#F43F5E'; // rose-500
  }
}

// ---------------------------------------------------------------------------
// usePrefersReducedMotion (lightweight inline helper)
// ---------------------------------------------------------------------------

function useDetectReducedMotion(override: boolean | undefined): boolean {
  const [auto, setAuto] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => setAuto(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return override ?? auto;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function KbChangeTree(props: KbChangeTreeProps): React.ReactElement | null {
  const {
    changesetId,
    mode = 'full',
    autoAcceptAncestors = true,
    reducedMotion: reducedMotionOverride,
    fetchPreview,
    apiContext,
    onOpenFile,
    onOpenInSyside,
    sysideAvailable = true,
  } = props;

  const store = props.store ?? getOrCreateDefaultStore(apiContext);

  const reducedMotion = useDetectReducedMotion(reducedMotionOverride);

  const changeset = useStore(store, (s: ChangesetSlice) => s.changesetsById[changesetId]) as
    | KbChangeset
    | undefined;
  const entityStates = useStore(
    store,
    (s: ChangesetSlice) => s.entityStatesByChangesetId[changesetId],
  ) as KbEntityState[] | undefined;
  const serverDependencyWarnings = useStore(
    store,
    (s: ChangesetSlice) => s.dependencyWarningsByEntityId,
  ) as Record<string, string>;
  const cycleEntity = useStore(store, (s: ChangesetSlice) => s.cycleEntity);
  const acceptEntity = useStore(store, (s: ChangesetSlice) => s.acceptEntity);
  const rejectEntity = useStore(store, (s: ChangesetSlice) => s.rejectEntity);
  const setEntityPending = useStore(store, (s: ChangesetSlice) => s.setEntityPending);
  const acceptAll = useStore(store, (s: ChangesetSlice) => s.acceptAll);
  const rejectAll = useStore(store, (s: ChangesetSlice) => s.rejectAll);
  const retryEntity = useStore(store, (s: ChangesetSlice) => s.retryEntity);
  const loadChangeset = useStore(store, (s: ChangesetSlice) => s.loadChangeset);
  // KB-undo footer affordance — pulls the ring-buffer + in-flight markers
  // from the slice so the button can disable itself between dispatch and
  // the matching `kb:changesetUndone` SSE round-trip.
  const availableUndoChangesets = useStore(
    store,
    (s: ChangesetSlice) => s.availableUndoChangesets,
  ) as AvailableUndoChangeset[];
  const undoInFlightById = useStore(
    store,
    (s: ChangesetSlice) => s.undoInFlightById,
  ) as Record<string, UndoInFlight>;
  const dispatchUndoLastChangeset = useStore(
    store,
    (s: ChangesetSlice) => s.dispatchUndoLastChangeset,
  );
  const setUndoError = useStore(store, (s: ChangesetSlice) => s.setUndoError);

  // Lazy load if the slice is empty
  useEffect(() => {
    if (!changeset) {
      void loadChangeset(changesetId);
    }
  }, [changeset, changesetId, loadChangeset]);

  // Local-side dependency warnings — set when the user clicks accept on a
  // blocked row before the server's 409 has arrived (parity with Lit
  // `_dependencyWarnings`, line 124).
  const [localDependencyWarnings, setLocalDependencyWarnings] = useState<Record<string, string>>({});

  // SR-announcer plumbing
  const [srAnnouncement, setSrAnnouncement] = useState('');
  const previousStatusesRef = useRef<Map<string, KbEntityStatus>>(new Map());

  // Context menu state
  const [menu, setMenu] = useState<{
    open: boolean;
    anchor: { x: number; y: number };
    entity: KbEntityChange | null;
  }>({ open: false, anchor: { x: 0, y: 0 }, entity: null });
  const [forceOpenSysml, setForceOpenSysml] = useState<string | null>(null);
  const [forceOpenSvg, setForceOpenSvg] = useState<string | null>(null);
  const [whyTooltipFor, setWhyTooltipFor] = useState<string | null>(null);

  // Sweep stale dependency warnings whenever entityStates change
  useEffect(() => {
    if (!entityStates) return;
    setLocalDependencyWarnings((prev) => {
      let mutated = false;
      const next: Record<string, string> = { ...prev };
      for (const id of Object.keys(prev)) {
        const ent = changeset?.entities.find((e) => e.entityId === id);
        if (!ent) {
          delete next[id];
          mutated = true;
          continue;
        }
        const stillBlocked = ent.dependsOn.some((depId) => {
          const status = statusFor(entityStates, depId);
          return status === 'rejected' || status === 'unresolvable';
        });
        if (!stillBlocked) {
          delete next[id];
          mutated = true;
        }
      }
      return mutated ? next : prev;
    });
  }, [entityStates, changeset]);

  // SR announcer — diff entityStates against previous snapshot
  useEffect(() => {
    if (!entityStates || !changeset) return;
    let lastChange: { entity: KbEntityChange; status: KbEntityStatus } | undefined;
    for (const entity of changeset.entities) {
      const newStatus = statusFor(entityStates, entity.entityId);
      const prevStatus = previousStatusesRef.current.get(entity.entityId);
      if (prevStatus !== undefined && prevStatus !== newStatus) {
        lastChange = { entity, status: newStatus };
      }
    }
    previousStatusesRef.current = new Map(
      changeset.entities.map((e) => [e.entityId, statusFor(entityStates, e.entityId)] as const),
    );
    if (lastChange) {
      const { accepted } = counts(changeset.entities, entityStates);
      const total = changeset.entities.length;
      const kindLabel = KIND_BADGE_LABEL[lastChange.entity.kind];
      setSrAnnouncement(
        `${kindLabel} ${lastChange.entity.entityId} ${lastChange.status}. ${accepted} of ${total} entities accepted.`,
      );
    }
  }, [entityStates, changeset]);

  // -------------------------------------------------------------------------
  // Empty state — render null when no changeset is loaded yet
  // -------------------------------------------------------------------------

  if (!changeset || !entityStates) return null;

  const groups = groupedEntities(changeset.entities);
  const aggregateCounts = counts(changeset.entities, entityStates);
  const failedRows = changeset.entities
    .map((e) => {
      const state = entityStates.find((s) => s.entityId === e.entityId);
      return state?.status === 'failed' ? { entity: e, state } : null;
    })
    .filter((r): r is { entity: KbEntityChange; state: KbEntityState } => r !== null);

  // -------------------------------------------------------------------------
  // Action handlers — DR-3 + dependency-violation guard (AC #5)
  // -------------------------------------------------------------------------

  const handleAccept = useCallback(
    async (entity: KbEntityChange): Promise<void> => {
      const blocker = findBlockingAncestor(entity, entityStates);
      if (blocker) {
        setLocalDependencyWarnings((prev) => ({
          ...prev,
          [entity.entityId]: `Cannot accept — depends on \`${blocker}\` which is rejected`,
        }));
        return;
      }
      // Clear stale warning
      setLocalDependencyWarnings((prev) => {
        if (!(entity.entityId in prev)) return prev;
        const next = { ...prev };
        delete next[entity.entityId];
        return next;
      });
      // Auto-accept-ancestors (DR-3)
      if (autoAcceptAncestors && entity.dependsOn.length > 0) {
        for (const depId of entity.dependsOn) {
          const status = statusFor(entityStates, depId);
          if (status === 'accepted' || status === 'applied') continue;
          await acceptEntity(changesetId, depId);
        }
      }
      await acceptEntity(changesetId, entity.entityId);
    },
    [acceptEntity, autoAcceptAncestors, changesetId, entityStates],
  );

  const handleCycle = useCallback(
    async (entity: KbEntityChange, next: KbEntityStatus): Promise<void> => {
      if (next === 'accepted') {
        await handleAccept(entity);
      } else if (next === 'rejected') {
        // Clear any local warning
        setLocalDependencyWarnings((prev) => {
          if (!(entity.entityId in prev)) return prev;
          const np = { ...prev };
          delete np[entity.entityId];
          return np;
        });
        await rejectEntity(changesetId, entity.entityId);
      } else if (next === 'pending') {
        setEntityPending(changesetId, entity.entityId);
      }
    },
    [changesetId, handleAccept, rejectEntity, setEntityPending],
  );

  const handleMenuAction = useCallback(
    async (action: ReviewChipMenuAction): Promise<void> => {
      const e = menu.entity;
      if (!e) return;
      switch (action.kind) {
        case 'accept':
          await handleAccept(e);
          break;
        case 'reject':
          await rejectEntity(changesetId, e.entityId);
          break;
        case 'pending':
          setEntityPending(changesetId, e.entityId);
          break;
        case 'open-file':
          onOpenFile?.(e);
          break;
        case 'open-in-syside':
          onOpenInSyside?.(e);
          break;
        case 'show-sysml-preview':
          setForceOpenSysml(e.entityId);
          break;
        case 'show-svg-preview':
          setForceOpenSvg(e.entityId);
          break;
        case 'why-proposed':
          setWhyTooltipFor(e.entityId);
          break;
        default:
          break;
      }
      setMenu({ open: false, anchor: { x: 0, y: 0 }, entity: null });
    },
    [changesetId, handleAccept, menu.entity, onOpenFile, onOpenInSyside, rejectEntity, setEntityPending],
  );

  // -------------------------------------------------------------------------
  // Keyboard handler (AC #8)
  // -------------------------------------------------------------------------

  const onTreeKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLElement>): void => {
      // Cmd/Ctrl+Shift+A — reject all (UX §6.2.6 — divergence from Lit's Ctrl+Shift+R)
      if ((ev.metaKey || ev.ctrlKey) && ev.shiftKey && (ev.key === 'a' || ev.key === 'A')) {
        ev.preventDefault();
        void rejectAll(changesetId);
        return;
      }
      // Cmd/Ctrl+A — accept all
      if ((ev.metaKey || ev.ctrlKey) && (ev.key === 'a' || ev.key === 'A')) {
        ev.preventDefault();
        void acceptAll(changesetId);
        return;
      }
      // Plain alpha keys — only when no modifier
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;

      const target = ev.target as HTMLElement | null;
      const row = target?.closest('[role="treeitem"][data-entity-id]') as HTMLElement | null;
      if (!row) return;
      const entityId = row.getAttribute('data-entity-id');
      if (!entityId) return;
      const entity = changeset.entities.find((e) => e.entityId === entityId);
      if (!entity) return;

      if (ev.key === ' ' || ev.key === 'Spacebar') {
        ev.preventDefault();
        void handleAccept(entity);
        return;
      }
      if (ev.key === 'a' || ev.key === 'A') {
        ev.preventDefault();
        void handleAccept(entity);
        return;
      }
      if (ev.key === 'r' || ev.key === 'R') {
        ev.preventDefault();
        void rejectEntity(changesetId, entityId);
        return;
      }
      if (ev.key === 'p' || ev.key === 'P') {
        ev.preventDefault();
        setEntityPending(changesetId, entityId);
        return;
      }
      if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        const all = Array.from(
          ev.currentTarget.querySelectorAll<HTMLElement>('[role="treeitem"][data-entity-id]'),
        );
        const idx = all.indexOf(row);
        const nextEl = all[Math.min(idx + 1, all.length - 1)];
        nextEl?.focus();
        return;
      }
      if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        const all = Array.from(
          ev.currentTarget.querySelectorAll<HTMLElement>('[role="treeitem"][data-entity-id]'),
        );
        const idx = all.indexOf(row);
        const prevEl = all[Math.max(idx - 1, 0)];
        prevEl?.focus();
        return;
      }
      if (ev.key === 'Enter') {
        ev.preventDefault();
        onOpenFile?.(entity);
        return;
      }
    },
    [
      acceptAll,
      changeset.entities,
      changesetId,
      handleAccept,
      onOpenFile,
      rejectAll,
      rejectEntity,
      setEntityPending,
    ],
  );

  // -------------------------------------------------------------------------
  // KB-undo footer handler (FR-DIFF-N "Undo last change")
  // -------------------------------------------------------------------------

  const topUndo = availableUndoChangesets[0];
  const topUndoInFlight =
    topUndo !== undefined ? undoInFlightById[topUndo.changesetId] : undefined;
  const undoDisabled =
    topUndo === undefined ||
    (topUndoInFlight !== undefined && topUndoInFlight.errorMessage === undefined);
  // Pull the most-recent in-flight error across the entire stack for the
  // inline 5-second error banner. We only ever show one at a time.
  const undoError = useMemo<UndoInFlight | undefined>(() => {
    for (const e of availableUndoChangesets) {
      const m = undoInFlightById[e.changesetId];
      if (m?.errorMessage !== undefined && m.errorMessage.length > 0) return m;
    }
    return undefined;
  }, [availableUndoChangesets, undoInFlightById]);

  // Auto-clear the inline error after ~5s per the story spec.
  useEffect(() => {
    if (undoError === undefined) return undefined;
    const timer = setTimeout(() => {
      setUndoError(undoError.changesetId, null);
    }, 5000);
    return () => clearTimeout(timer);
  }, [undoError, setUndoError]);

  const handleUndoClick = useCallback(
    (targetChangesetId?: string): void => {
      void dispatchUndoLastChangeset(targetChangesetId);
    },
    [dispatchUndoLastChangeset],
  );

  // -------------------------------------------------------------------------
  // Combined preview fetch (default: hit API; tests inject a stub)
  // -------------------------------------------------------------------------

  const previewFetcher = useMemo(
    () =>
      fetchPreview ??
      ((csId: string, entityId: string) =>
        changesetApi.getPreviewSvg(apiContext ?? { baseUrl: '', token: null }, csId, entityId)),
    [fetchPreview, apiContext],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const rootStyle: React.CSSProperties =
    mode === 'inline'
      ? { maxWidth: 640, padding: '0.5rem' }
      : { padding: '0.5rem' };

  return (
    <section
      className={`kb-change-tree ${mode === 'inline' ? 'mode-inline max-w-[640px]' : 'mode-full'}`}
      data-testid={`kb-change-tree-${changesetId}`}
      data-mode={mode}
      role="tree"
      // LOW-5 — WAI-ARIA `aria-checked` on `<li role="treeitem">` is only
      // valid when the containing tree advertises multi-select via
      // `aria-multiselectable="true"`. The per-entity accept toggle is a
      // multi-select interaction by design (each leaf accepts independently),
      // so the tree IS multiselectable — declare it.
      aria-multiselectable="true"
      aria-label="Proposed knowledge-base changes"
      style={rootStyle}
      onKeyDown={onTreeKeyDown}
    >
      <h3 className="proposal-title" style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>
        {changeset.intent}
      </h3>

      {mode === 'full' && (
        <div className="changeset-header-actions" style={{ marginBottom: '0.5rem', display: 'flex', gap: '0.25rem' }}>
          <button
            type="button"
            data-testid="changeset-accept-all"
            onClick={() => {
              void acceptAll(changesetId);
            }}
          >
            Accept all
          </button>
          <button
            type="button"
            data-testid="changeset-reject-all"
            onClick={() => {
              void rejectAll(changesetId);
            }}
          >
            Reject all
          </button>
          <button
            type="button"
            data-testid="changeset-apply-accepted"
            disabled
            title="Coming in W4-S7"
          >
            Apply accepted
          </button>
          <button
            type="button"
            data-testid="changeset-open-in-chat"
            disabled
            title="Coming in W4-S7"
          >
            Open in chat
          </button>
        </div>
      )}

      <ul className="group-list" role="group" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {groups.map((g) => (
          <li
            key={g.kind}
            className={`group group-${g.kind}`}
            role="treeitem"
            aria-expanded="true"
          >
            <div
              className="group-header"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0' }}
            >
              <span className="group-label" style={{ flex: 1, fontWeight: 600 }}>
                {KIND_GROUP_LABEL[g.kind]} ({g.entities.length})
              </span>
              <button
                type="button"
                className="btn-accept-all"
                aria-label="Accept all entities in changeset"
                data-testid={`group-accept-all-${g.kind}`}
                onClick={() => {
                  void acceptAll(changesetId);
                }}
              >
                Accept all
              </button>
              <button
                type="button"
                className="btn-reject-all"
                aria-label="Reject all entities in changeset"
                data-testid={`group-reject-all-${g.kind}`}
                onClick={() => {
                  void rejectAll(changesetId);
                }}
              >
                Reject all
              </button>
            </div>
            <ul
              className="leaf-list"
              role="group"
              style={{ listStyle: 'none', margin: 0, padding: 0 }}
            >
              {g.entities.map((entity) => {
                const status = statusFor(entityStates, entity.entityId);
                const accepted = status === 'accepted' || status === 'applied';
                const blocker = findBlockingAncestor(entity, entityStates);
                const blocked = blocker !== undefined;
                const localWarn = localDependencyWarnings[entity.entityId];
                const serverWarn = serverDependencyWarnings[entity.entityId];
                // Auto-surface a soft-warning when the row is blocked by a
                // rejected ancestor — parity with the Lit precedent's
                // behaviour AFTER the user clicks Accept (here surfaced
                // pro-actively so AT users see the constraint without
                // having to attempt the action first).
                const blockedWarn = blocker !== undefined
                  ? `Cannot accept — depends on \`${blocker}\` which is rejected`
                  : undefined;
                const dependencyWarning = localWarn ?? serverWarn ?? blockedWarn;
                const failureMessage = entityStates.find((s) => s.entityId === entity.entityId)?.failureMessage;

                return (
                  <li
                    key={entity.entityId}
                    className="leaf"
                    role="treeitem"
                    tabIndex={0}
                    aria-selected={accepted}
                    aria-checked={accepted}
                    data-entity-id={entity.entityId}
                    data-status={status}
                    style={{ padding: '0.25rem 0', borderTop: '1px solid var(--color-taupe-300)' }}
                  >
                    <div
                      className="leaf-row"
                      style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}
                    >
                      <span
                        className={`status-icon status-${status}`}
                        aria-hidden="true"
                        style={{ minWidth: '1.25em', textAlign: 'center' }}
                      >
                        {statusIcon(status)}
                      </span>
                      {typeof entity.changeIndex === 'number' && entity.changeIndex >= 1 ? (
                        <span
                          data-testid={`change-index-badge-${entity.entityId}`}
                          data-change-index={entity.changeIndex}
                          aria-label={`Change ${entity.changeIndex}`}
                          style={{
                            display: 'inline-block',
                            minWidth: 22,
                            height: 22,
                            padding: '0 6px',
                            borderRadius: '50%',
                            background: changeIndexBadgeBg(entity.op),
                            color: '#FFFFFF',
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: '22px',
                            textAlign: 'center',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
                          }}
                        >
                          {entity.changeIndex}
                        </span>
                      ) : null}
                      <span className="entity-id" style={{ fontFamily: 'monospace' }}>
                        {entity.entityId}
                      </span>
                      <span className="badge kind">{KIND_BADGE_LABEL[entity.kind]}</span>
                      <span className="badge op">{entity.op}</span>
                      <span style={{ marginLeft: 'auto' }}>
                        <ReviewChip
                          entityId={entity.entityId}
                          entityKind={entity.kind}
                          status={status}
                          blocked={blocked}
                          blockedReason={
                            blocker !== undefined
                              ? `Cannot accept — depends on \`${blocker}\` which is rejected.`
                              : undefined
                          }
                          reducedMotion={reducedMotion}
                          onCycle={(next) => {
                            void handleCycle(entity, next);
                          }}
                          onContextMenu={(anchor) =>
                            setMenu({ open: true, anchor, entity })
                          }
                        />
                      </span>
                      {entity.rationale && (
                        <div className="rationale" style={{ flex: '1 1 100%', fontSize: '0.85em' }}>
                          {entity.rationale}
                        </div>
                      )}
                      {failureMessage !== undefined && (
                        <div
                          className="rationale"
                          data-failure="true"
                          data-testid={`failure-message-${entity.entityId}`}
                          style={{ flex: '1 1 100%', fontSize: '0.85em', color: 'var(--color-audi-red)' }}
                        >
                          {failureMessage}
                        </div>
                      )}
                      {whyTooltipFor === entity.entityId && (
                        <div
                          role="tooltip"
                          data-testid={`why-tooltip-${entity.entityId}`}
                          onClick={() => setWhyTooltipFor(null)}
                          style={{
                            flex: '1 1 100%',
                            background: 'var(--color-taupe-900)',
                            color: '#FFFFFF',
                            padding: '0.375rem 0.5rem',
                            borderRadius: '2px',
                            fontSize: '0.85em',
                          }}
                        >
                          {entity.rationale ?? '(no rationale provided by agent)'}
                        </div>
                      )}
                    </div>

                    <RowDisclosure
                      changesetId={changesetId}
                      entity={entity}
                      dependencyWarning={dependencyWarning}
                      forceOpenSysml={forceOpenSysml === entity.entityId}
                      forceOpenSvg={forceOpenSvg === entity.entityId}
                      fetchPreview={previewFetcher}
                    />
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>

      {failedRows.length > 0 && (
        <section
          data-testid="failed-entities-footer"
          className="failed-entities-footer"
          aria-label={`Failed entities (${failedRows.length})`}
          style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-taupe-500)' }}
        >
          <h4 style={{ margin: 0, fontSize: '0.9em' }}>Failed entities ({failedRows.length})</h4>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {failedRows.map(({ entity, state }) => (
              <li
                key={entity.entityId}
                data-testid={`failed-row-${entity.entityId}`}
                style={{ display: 'flex', gap: '0.5rem', padding: '0.25rem 0' }}
              >
                <span aria-hidden="true">✗</span>
                <span style={{ flex: 1 }}>
                  {entity.entityId}
                  {state.failureMessage && (
                    <span style={{ marginLeft: '0.5rem', color: 'var(--color-taupe-700)' }}>
                      reason: {state.failureMessage}
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  data-testid={`failed-retry-${entity.entityId}`}
                  onClick={() => {
                    void retryEntity(changesetId, entity.entityId);
                  }}
                >
                  Retry
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {availableUndoChangesets.length > 0 && (
        <div
          className="undo-footer"
          data-testid="kb-undo-footer"
          style={{
            marginTop: '0.5rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid #C8BC9E',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            data-testid="kb-undo-button"
            aria-label="Undo last applied changeset"
            disabled={undoDisabled}
            onClick={() => handleUndoClick()}
            style={{ padding: '0.25rem 0.5rem' }}
          >
            Undo last change
          </button>
          <details data-testid="kb-undo-popover">
            <summary
              aria-label="Show recent undoable changesets"
              style={{ cursor: 'pointer', fontSize: '0.85em', color: '#8B8678' }}
            >
              Recent ({availableUndoChangesets.length})
            </summary>
            <ul
              data-testid="kb-undo-list"
              role="list"
              style={{ listStyle: 'none', margin: '0.25rem 0', padding: 0 }}
            >
              {availableUndoChangesets.map((entry) => {
                const inFlight = undoInFlightById[entry.changesetId];
                const disabled =
                  inFlight !== undefined && inFlight.errorMessage === undefined;
                return (
                  <li
                    key={entry.changesetId}
                    data-testid={`kb-undo-entry-${entry.changesetId}`}
                    style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                      padding: '0.125rem 0',
                      fontSize: '0.85em',
                    }}
                  >
                    <button
                      type="button"
                      data-testid={`kb-undo-entry-button-${entry.changesetId}`}
                      aria-label={`Undo changeset ${entry.changesetId}`}
                      disabled={disabled}
                      onClick={() => handleUndoClick(entry.changesetId)}
                      style={{ padding: '0 0.25rem' }}
                    >
                      Undo
                    </button>
                    <span style={{ fontFamily: 'monospace' }}>
                      {entry.changesetId}
                    </span>
                    <span style={{ color: '#8B8678' }}>
                      {entry.fileCount} file{entry.fileCount === 1 ? '' : 's'} ·{' '}
                      {entry.acceptedAt}
                    </span>
                  </li>
                );
              })}
            </ul>
          </details>
          {undoError !== undefined && undoError.errorMessage !== undefined && (
            <span
              role="alert"
              data-testid="kb-undo-error"
              style={{ color: '#A23B25', fontSize: '0.85em' }}
            >
              {undoError.errorMessage}
            </span>
          )}
        </div>
      )}

      <div
        className="footer"
        data-testid="changeset-footer-counts"
        style={{ marginTop: '0.5rem', fontSize: '0.85em', color: 'var(--color-taupe-700)' }}
      >
        {aggregateCounts.accepted} accepted · {aggregateCounts.rejected} rejected ·{' '}
        {aggregateCounts.pending} pending · {aggregateCounts.failed} failed
      </div>

      <div
        className="sr-only"
        aria-live="polite"
        data-testid="sr-announcer"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {srAnnouncement}
      </div>

      {menu.open && menu.entity && (
        <ReviewChipContextMenu
          open={menu.open}
          anchor={menu.anchor}
          entity={menu.entity}
          status={statusFor(entityStates, menu.entity.entityId)}
          blocked={findBlockingAncestor(menu.entity, entityStates) !== undefined}
          sysideAvailable={sysideAvailable}
          onAction={(action) => {
            void handleMenuAction(action);
          }}
          onClose={() =>
            setMenu({ open: false, anchor: { x: 0, y: 0 }, entity: null })
          }
        />
      )}
    </section>
  );
}
